/**
 * Module dependencies.
 */

import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import carBlack from 'src/assets/landingpage/car_black.webp';
import carWhite from 'src/assets/landingpage/car_white.webp';
import styled from 'styled-components';

/**
 * Hero image sizes.
 */

const HERO_IMAGE_SIZES = '(max-width: 767px) 100vw, 735px';

/**
 * `Container` styled component.
 */

const Container = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  position: relative;
  width: 100%;

  @media (max-width: 767px) {
    width: 100vw;
  }

  p {
    color: #0a0a28;
    font-family: Raleway, sans-serif;
    font-size: 72px;
    font-weight: 900;
    line-height: 80px;
    position: sticky;
    text-align: center;
    top: 0;
    width: 100%;

    @media (max-width: 767px) {
      width: 100%;
    }

    @media (max-width: 400px) {
      font-size: 30px;
      line-height: 36px;
    }

    @media (min-width: 400px) and (max-width: 767px) {
      font-size: 42px;
      line-height: 48px;
    }
  }

  > div {
    height: 444px;
    overflow: visible;
    position: relative;
    width: 735.28px;

    @media (max-width: 767px) {
      aspect-ratio: 735.28 / 444;
      height: auto;
      margin-left: calc(-50vw + 50%);
      margin-right: calc(-50vw + 50%);
      width: 100vw;
    }

    @media (min-width: 768px) and (max-width: 1023px) {
      aspect-ratio: 735.28 / 444;
      height: auto;
      margin-bottom: -32px;
      margin-top: 32px;
      width: 100%;
    }

    @media (max-width: 1119px) and (min-width: 1024px) {
      aspect-ratio: 735.28 / 444;
      height: auto;
      width: 100%;
    }

    .hero-car-image {
      inset: 0;
      pointer-events: none;
      position: absolute;

      @media (max-width: 767px) {
        height: 100%;
        width: 100%;
      }

      @media (max-width: 1119px) and (min-width: 768px) {
        height: 100%;
        width: 100%;
      }

      img {
        height: 100%;
        object-fit: contain;
        pointer-events: none;
        width: 100%;
      }
    }

    #hero-car-white {
      z-index: 2;
    }
  }
`;

const CarBlueMask = styled.div`
  height: 100vh;
  left: 0;
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  top: -100vh;
  transform: translateY(0);
  transition: transform 0.1s ease-out;
  width: 100%;
  z-index: 1;

  .hero-car-image {
    transform: translateY(100vh);

    @media (max-width: 767px) {
      height: 100%;
      width: 100%;
    }

    @media (max-width: 1119px) and (min-width: 768px) {
      height: 100%;
      width: 100%;
    }
  }
`;

/**
 * `HeroContent` component.
 */

export function HeroContent() {
  const { t } = useTranslation('landingpage');

  return (
    <Container data-name={'Hero text'} id={'hero-text-section'}>
      <p id={'hero-title'}>
        <span>{t('hero.title.prefix')}</span>

        {t('hero.title.brand')}
      </p>

      <div data-name={'Hero_car_no_bg 2'} id={'hero-car-image'}>
        <Image
          alt={t('hero.images.carBlack')}
          className={'hero-car-image'}
          fill
          priority
          sizes={HERO_IMAGE_SIZES}
          src={carBlack}
          style={{ objectFit: 'contain', pointerEvents: 'none' }}
        />

        <CarBlueMask id={'hero-car-blue-mask'}>
          <Image
            alt={t('hero.images.carBlue')}
            className={'hero-car-image'}
            fill
            sizes={HERO_IMAGE_SIZES}
            src={carWhite}
            style={{
              filter: 'sepia(1) saturate(3) hue-rotate(140deg) brightness(0.9)',
              objectFit: 'contain',
              pointerEvents: 'none'
            }}
          />
        </CarBlueMask>

        <Image
          alt={t('hero.images.carWhite')}
          className={'hero-car-image'}
          fill
          id={'hero-car-white'}
          sizes={HERO_IMAGE_SIZES}
          src={carWhite}
          style={{ objectFit: 'contain', opacity: 0, pointerEvents: 'none', transition: 'opacity 0.1s ease-out' }}
        />
      </div>
    </Container>
  );
}
